import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';

const NAV = [
  { to: '/', key: 'home', testid: 'nav-home' },
  { to: '/empresa', key: 'about', testid: 'nav-about' },
  { to: '/servicios', key: 'services', testid: 'nav-services' },
  { to: '/internacional', key: 'international', testid: 'nav-international' },
  { to: '/idi', key: 'sustainability', testid: 'nav-sustainability' },
  { to: '/contacto', key: 'contact', testid: 'nav-contact' },
];

export default function Header() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-sand/85 backdrop-blur-xl border-b border-forest/10'
          : 'bg-sand/0 border-b border-transparent'
      }`}
    >
      <div className="container-x flex items-center justify-between py-5 md:py-6">
        <Link to="/" className="group flex items-baseline gap-3" data-testid="logo-link">
          <span className="font-serif text-[22px] md:text-[24px] tracking-tight text-forest">
            Gravats <span className="italic font-light">Maresme</span>
          </span>
          <span className="hidden md:inline-block text-[10px] uppercase tracking-[0.25em] text-forest/45">
            Est. 1981
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              data-testid={n.testid}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.22em] transition-colors duration-300 link-underline ${
                  isActive ? 'text-forest' : 'text-forest/60 hover:text-forest'
                }`
              }
            >
              {t.nav[n.key]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          <div className="hidden md:block">
            <LanguageSwitcher tone="dark" />
          </div>
          <button
            type="button"
            className="lg:hidden p-2 -mr-2 text-forest"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t.nav.close : t.nav.menu}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? 'max-h-[640px]' : 'max-h-0'
        } bg-sand border-t border-forest/10`}
        data-testid="mobile-menu"
      >
        <div className="container-x py-8 flex flex-col gap-5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              data-testid={`mobile-${n.testid}`}
              className={({ isActive }) =>
                `font-serif text-3xl tracking-tight ${
                  isActive ? 'text-forest' : 'text-forest/55'
                }`
              }
            >
              {t.nav[n.key]}
            </NavLink>
          ))}
          <div className="pt-4 border-t border-forest/10">
            <LanguageSwitcher tone="dark" />
          </div>
        </div>
      </div>
    </header>
  );
}
